import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Bookmark, BookmarkPlus, Pin, PinOff, X, Check, Edit2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SavedViewsBar — pin filter combinations for the current path.
 *
 * Props:
 *   path             - logical path key (e.g. "/leads"). Required.
 *   currentQuery     - current URL query string ("source=mhm_lite&focus=...") used when saving.
 *   activeFilterCount- shown on the Save button to discourage saving empty views.
 *
 * Behaviour:
 *   - Pinned views render as inline chips (pillbox).
 *   - Other views available via "All views" popover.
 *   - Clicking a chip navigates to `${path}?${view.query}`.
 *   - Save dialog accepts a name; auto-disables submit when empty or when no filters active.
 *   - Inline edit (rename) and delete actions inside the popover.
 */
export default function SavedViewsBar({ path, currentQuery, activeFilterCount = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const fetchViews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/saved-views`, { params: { path } });
      setViews(res.data || []);
    } catch (e) {
      // silent — saved views are non-critical
    }
  }, [path]);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  const isCurrentView = (view) => {
    const norm = (q) => new URLSearchParams(q || "").toString();
    return norm(view.query) === norm(currentQuery) && location.pathname === view.path;
  };

  const navigateToView = (view) => {
    navigate(`${view.path}${view.query ? `?${view.query}` : ""}`);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/saved-views`, {
        name: name.trim(),
        path,
        query: currentQuery || "",
        pinned: false,
      });
      setViews((prev) => [res.data, ...prev]);
      setName("");
      setSaveOpen(false);
      toast.success(`View "${res.data.name}" saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save view");
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (view) => {
    try {
      const res = await axios.put(`${API}/saved-views/${view.view_id}`, {
        pinned: !view.pinned,
      });
      setViews((prev) => prev.map((v) => (v.view_id === view.view_id ? res.data : v)));
    } catch (e) {
      toast.error("Failed to update view");
    }
  };

  const renameView = async (view) => {
    const newName = editingName.trim();
    if (!newName || newName === view.name) {
      setEditingId(null);
      return;
    }
    try {
      const res = await axios.put(`${API}/saved-views/${view.view_id}`, { name: newName });
      setViews((prev) => prev.map((v) => (v.view_id === view.view_id ? res.data : v)));
      setEditingId(null);
      toast.success("View renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const deleteView = async (view) => {
    if (!window.confirm(`Delete view "${view.name}"?`)) return;
    try {
      await axios.delete(`${API}/saved-views/${view.view_id}`);
      setViews((prev) => prev.filter((v) => v.view_id !== view.view_id));
      toast.success("View deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const pinned = views.filter((v) => v.pinned);
  const others = views.filter((v) => !v.pinned);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="saved-views-bar">
      {pinned.map((v) => (
        <button
          key={v.view_id}
          type="button"
          onClick={() => navigateToView(v)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            isCurrentView(v)
              ? "bg-violet-600 text-white border-violet-600 shadow-sm"
              : "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"
          }`}
          data-testid={`saved-view-${v.view_id}`}
          title={`${v.path}?${v.query}`}
        >
          <Pin className="w-3 h-3" /> {v.name}
        </button>
      ))}

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100"
            data-testid="saved-views-menu-btn"
          >
            <Bookmark className="w-3 h-3" />
            {views.length > 0 ? `My views (${views.length})` : "My views"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Saved views
            </p>
            <button
              type="button"
              disabled={activeFilterCount === 0}
              onClick={() => {
                setPopoverOpen(false);
                setSaveOpen(true);
              }}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
              data-testid="open-save-view-dialog-btn"
              title={activeFilterCount === 0 ? "Apply filters first" : "Save current filter combination"}
            >
              <BookmarkPlus className="w-3 h-3" /> Save current
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto py-1" data-testid="saved-views-list">
            {views.length === 0 && (
              <p className="px-3 py-4 text-xs text-slate-500 text-center">
                No saved views yet. Apply a filter combination and click "Save current" to pin it for later.
              </p>
            )}
            {[...pinned, ...others].map((v) => (
              <div
                key={v.view_id}
                className={`group px-3 py-2 hover:bg-slate-50 flex items-center gap-2 ${
                  isCurrentView(v) ? "bg-violet-50" : ""
                }`}
                data-testid={`saved-view-row-${v.view_id}`}
              >
                {editingId === v.view_id ? (
                  <Input
                    autoFocus
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") renameView(v);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    onBlur={() => renameView(v)}
                    className="h-7 text-sm"
                    data-testid={`rename-view-input-${v.view_id}`}
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        navigateToView(v);
                        setPopoverOpen(false);
                      }}
                      className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-violet-700 truncate"
                      data-testid={`apply-saved-view-${v.view_id}`}
                    >
                      {v.pinned && <Pin className="w-3 h-3 inline -mt-0.5 mr-1 text-violet-500" />}
                      {v.name}
                    </button>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => togglePin(v)}
                        className="p-1 rounded hover:bg-slate-200 text-slate-500"
                        title={v.pinned ? "Unpin" : "Pin to filter bar"}
                        data-testid={`toggle-pin-${v.view_id}`}
                      >
                        {v.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(v.view_id);
                          setEditingName(v.name);
                        }}
                        className="p-1 rounded hover:bg-slate-200 text-slate-500"
                        title="Rename"
                        data-testid={`rename-view-${v.view_id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteView(v)}
                        className="p-1 rounded hover:bg-red-100 text-slate-500 hover:text-red-600"
                        title="Delete"
                        data-testid={`delete-view-${v.view_id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              Pin this filter combination for one-click access later.
            </p>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Input
                autoFocus
                placeholder='e.g. "Hot leads from Facebook this week"'
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && name.trim()) handleSave();
                }}
                maxLength={60}
                data-testid="save-view-name-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                {currentQuery ? `Saves: ?${currentQuery}` : "No filters applied yet."}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setSaveOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-violet-600 hover:bg-violet-700"
                disabled={!name.trim() || saving}
                onClick={handleSave}
                data-testid="save-view-submit-btn"
              >
                <Check className="w-4 h-4 mr-1" /> Save view
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
